/**
 * CameraWireframes — renders camera frustum wireframes in the main scene when
 * wireframe mode is active, one per camera_pip() definition.
 *
 * Each frustum shows:
 *  - A pyramid from the camera position to the four corners of the near plane
 *  - A rectangle connecting those four corners (the "viewport")
 *  - A line from camera position to the look-at target
 *
 * This follows the same pattern as LightWireframes.tsx.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import type { PiPCamera } from '@/worker/types';

/** Cyan teal — distinct from the light wireframe colors */
const CAMERA_WIREFRAME_COLOR = new THREE.Color(0x00bcd4);

function CameraFrustumWireframe({
  camera,
  opacity,
}: {
  camera: PiPCamera;
  opacity: number;
}) {
  const lineSegments = useMemo(() => {
    const pos = new THREE.Vector3(...camera.position);
    const target = new THREE.Vector3(...camera.target);

    // Direction from camera to target
    const dir = new THREE.Vector3().subVectors(target, pos);
    const dist = dir.length();
    if (dist < 0.001) {
      // Camera and target are at the same position — just draw a crosshair
      const size = 2;
      const points = [
        new THREE.Vector3(pos.x - size, pos.y, pos.z),
        new THREE.Vector3(pos.x + size, pos.y, pos.z),
        new THREE.Vector3(pos.x, pos.y - size, pos.z),
        new THREE.Vector3(pos.x, pos.y + size, pos.z),
        new THREE.Vector3(pos.x, pos.y, pos.z - size),
        new THREE.Vector3(pos.x, pos.y, pos.z + size),
      ];
      const geom = new THREE.BufferGeometry().setFromPoints(points);
      const mat = new THREE.LineBasicMaterial({
        color: CAMERA_WIREFRAME_COLOR,
        transparent: true,
        opacity,
      });
      return new THREE.LineSegments(geom, mat);
    }

    dir.normalize();

    // Build local coordinate system for the frustum rectangle
    const up = new THREE.Vector3(0, 1, 0);
    if (Math.abs(dir.y) > 0.99) up.set(1, 0, 0);
    const right = new THREE.Vector3().crossVectors(up, dir).normalize();
    const actualUp = new THREE.Vector3().crossVectors(dir, right).normalize();

    // Frustum size: draw the near-plane rectangle at a fixed fraction of the
    // camera-to-target distance, scaled by the FOV.
    const fovRad = (camera.fov * Math.PI) / 180;
    // Use 40% of the distance to the target as the near-plane draw distance
    const nearDist = dist * 0.4;
    const halfH =
      camera.type === 'orthographic'
        ? nearDist * 0.3 // orthographic: fixed scale
        : Math.tan(fovRad / 2) * nearDist;
    const halfW = halfH * (camera.width / Math.max(camera.height, 0.001));

    // Center of the near-plane rectangle
    const center = new THREE.Vector3().copy(pos).addScaledVector(dir, nearDist);

    // Four corners of the near-plane rectangle
    const tl = center
      .clone()
      .addScaledVector(right, -halfW)
      .addScaledVector(actualUp, halfH);
    const tr = center
      .clone()
      .addScaledVector(right, halfW)
      .addScaledVector(actualUp, halfH);
    const br = center
      .clone()
      .addScaledVector(right, halfW)
      .addScaledVector(actualUp, -halfH);
    const bl = center
      .clone()
      .addScaledVector(right, -halfW)
      .addScaledVector(actualUp, -halfH);

    const points: THREE.Vector3[] = [
      // Near-plane rectangle
      tl,
      tr,
      tr,
      br,
      br,
      bl,
      bl,
      tl,
      // Frustum pyramid lines from camera position to rectangle corners
      pos.clone(),
      tl,
      pos.clone(),
      tr,
      pos.clone(),
      br,
      pos.clone(),
      bl,
      // Line from camera to look-at target
      pos.clone(),
      target.clone(),
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: CAMERA_WIREFRAME_COLOR,
      transparent: true,
      opacity,
    });
    return new THREE.LineSegments(geometry, material);
  }, [camera, opacity]);

  return <primitive object={lineSegments} />;
}

export function CameraWireframes({
  pipCameras,
  wireframeOpacity,
}: {
  pipCameras: PiPCamera[];
  wireframeOpacity: number;
}) {
  if (wireframeOpacity <= 0 || pipCameras.length === 0) return null;

  return (
    <>
      {pipCameras.map((cam, i) => (
        <CameraFrustumWireframe
          key={`${cam.position.join(',')}-${i}`}
          camera={cam}
          opacity={wireframeOpacity}
        />
      ))}
    </>
  );
}
